import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

type BaseContextShape = {
  filterFunnel: any
  globalData: any
  menuData: any
  updatedAt: string
}

type Ctx = {
  data: BaseContextShape | null
  error: Error | null
  loading: boolean
  reload: () => void
}

const BaseContext = createContext<Ctx | undefined>(undefined)

// module-level cache so multiple consumers don’t refetch
let cachedData: BaseContextShape | null = null
let inflight: null | Promise<BaseContextShape> = null

async function fetchBase(): Promise<BaseContextShape> {
  const res = await fetch("/BaseContext.json", { credentials: "same-origin" })
  if (!res.ok) throw new Error(`BaseContext.json ${res.status}`)
  return res.json()
}

export const BaseContextProvider: React.FC<{
  children: React.ReactNode
  initial?: BaseContextShape | null
}> = ({ children, initial = null }) => {
  const [data, setData] = useState<BaseContextShape | null>(
    initial ?? cachedData
  )
  const [loading, setLoading] = useState<boolean>(!initial && !cachedData)
  const [error, setError] = useState<Error | null>(null)

  const load = () => {
    if (cachedData) {
      setData(cachedData)
      setLoading(false)
      return
    }
    setLoading(true)
    if (!inflight) inflight = fetchBase().then((d) => (cachedData = d))
    inflight
      .then((d) => setData(d))
      .catch((e) => setError(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!data) load()
  }, []) // client-only fetch

  const value = useMemo(
    () => ({ data, error, loading, reload: load }),
    [data, loading, error]
  )
  return <BaseContext.Provider value={value}>{children}</BaseContext.Provider>
}

export const useBaseContext = () => {
  const ctx = useContext(BaseContext)
  if (!ctx)
    throw new Error("useBaseContext must be used within BaseContextProvider")
  return ctx
}
